import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { breadcrumbParts } from "../../../models/breadcrumb-parts";
import { BreadcrumbList } from "../../ui/breadcrumb-list/breadcrumb-list";
import { Header } from "../../ui/header/header";
import { Input } from "../../ui/input/input";
import { SideBar } from "../../ui/sidebar/sidebar";
import { SortListType, Table } from "../../table/table";
import { useAppSelector } from "../../../app/hooks";
import { DateFormatter } from "../../../utilities";
import { CompanyUsersBody, CompanyUsersDetail } from "../../../models/company-users-data";
import { Pager } from "../../ui/pager/pager";
import { useDispatch } from "react-redux";
import { dialogAction } from "../../../slices/dialog-slice";
import { apiIsp, ApiManager } from "../../../managers/api-manager";
import { CompanyAffiliationTable } from "./components/company-affiliation-table";
import { Button } from "../../ui/button/button";
import { CompanyUserEditDialog } from "../../dialog/company-user-edit-dialog";
import { ApiGetSearchCompanyUsersResponse, SearchCompanyUsersResponseDataEntity } from "../../../module/api/api/isp-web/company-users/api-get-company-users.response";
import './company.scss';
import { useAsyncLogOperation } from "../../../hooks/use-async-log-operation";
import * as lodash from 'lodash';

export type HasBlurElement = {
  blur: () => void,
}

type SearchParam = {
  words?: string | undefined;
  sort_by?: number | undefined;
  highlow?: number | undefined;
  page?: number | undefined;
  id?: string | undefined;
}

export function checkBlurElement(elm: any): elm is HasBlurElement {
  return 'blur' in elm;
}
const apiManagerIsp = new ApiManager('isp');

const breadcrumbList = [
  breadcrumbParts.isp.home,
  breadcrumbParts.isp.companyUserSearch,
];

type ReInviteButtonProps = {
  handleClickReInvite: () => void,
  handleClickCancelInvite: () => void,
  isOwner?: boolean,
}

const ReInviteButton = (props: ReInviteButtonProps) => {
  const {
    handleClickReInvite,
    handleClickCancelInvite,
    isOwner,
  } = props;
  return (
    <div style={{ width: '100%' }}>
      <Button
        size="small"
        color="secondary"
        label="再招待"
        onClick={() => handleClickReInvite()}
      />
      {!isOwner && (
        <Button
          size="small"
          color="alert"
          label="取消し"
          onClick={() => handleClickCancelInvite()}
          style={{ width: '48px', height: '27px' }}
        />
      )}
    </div>
  )
}

const sortKey = {
  name: 1,
  mail: 2,
  created_at: 3,
  login_at: 4,
};

const companyUsersDetailSortKey = {
  company_code: 1,
  company_name: 2,
  display_name: 3,
  role: 4,
  status: 5,
  created_at: 6,
};

const highlowKey = {
  high: 0,
  low: 1,
};

const initialSortList: SortListType[] = [
  {
    index: 0,
    type: 'up',
    key: sortKey.name,
    active: '',
    callback: () => {
    },
  },
  {
    index: 1,
    type: 'up',
    key: sortKey.mail,
    active: '',
    callback: () => {
    },
  },
  {
    index: 2,
    type: 'up',
    key: sortKey.created_at,
    active: '',
    callback: () => {
    },
  },
  {
    index: 3,
    type: 'up',
    key: sortKey.login_at,
    active: '',
    callback: () => {
    },
  },
];

const initialCompanyUsersDetailSortList: SortListType[] = [
  {
    index: 0,
    type: 'up',
    key: companyUsersDetailSortKey.company_code,
    active: '',
    callback: () => {
    },
  },
  {
    index: 1,
    type: 'up',
    key: companyUsersDetailSortKey.company_name,
    active: '',
    callback: () => {
    },
  },
  {
    index: 2,
    type: 'up',
    key: companyUsersDetailSortKey.display_name,
    active: '',
    callback: () => {
    },
  },
  {
    index: 3,
    type: 'up',
    key: companyUsersDetailSortKey.role,
    active: '',
    callback: () => {
    },
  },
  {
    index: 4,
    type: 'up',
    key: companyUsersDetailSortKey.status,
    active: '',
    callback: () => {
    },
  },
  {
    index: 5,
    type: 'up',
    key: companyUsersDetailSortKey.created_at,
    active: '',
    callback: () => {
    },
  },
];

// テーブル要素用意
const head: string[] = [
  '名前(カナ)',
  'メールアドレス',
  '登録日',
  '最終ログイン',
];

export const CompanyUsersSearch = () => {
  const [searchCompanyUsersList, setSearchCompanyUsersList] = useState<SearchCompanyUsersResponseDataEntity[]>([]);
  const [companyUsersDetailList, setCompanyUsersDetailList] = useState<CompanyUsersDetail[]>([]);
  const [sortList, setSortList] = useState<SortListType[]>(initialSortList);
  const [companyUsersDetailSortList, setCompanyUsersDetailSortList] = useState<SortListType[]>(initialCompanyUsersDetailSortList);
  const dispatch = useDispatch();
  const operation = useAsyncLogOperation();
  const baseLog = useMemo(() => {
    return operation.isp;
  }, [operation]);
  const { userInfo } = useAppSelector((state) => state.loginIspUserInfo);
  const [freeWord, setFreeWord] = useState('');
  const [companyUsersBody, setCompanyUsersBody] = useState<Pick<
    CompanyUsersBody,
    'current_page' | 'last_page' | 'total' | 'per_page'
    >>({
      current_page: 0,
      last_page: 0,
      per_page: 0,
      total: 0,
  });
  const rowRef = useRef<HTMLTableRowElement>(null);
  const prevSearchParam = useRef<SearchParam>({});
  //　表示データの全件数
  const totalData = companyUsersBody.total;
  // 一度に表示するデータの件数
  const perData = companyUsersBody.per_page;
  // 現在のページ
  const [currentPage, setCurrentPage] = useState(companyUsersBody.current_page);
  // 表示開始のデータ番号
  const totalPage: number = useMemo(() => {
    const _totalPage = totalData % perData ?
      Math.floor(totalData / perData) : // 合計のページ数
      Math.floor(totalData / perData) - 1; // 合計のページ数(件数が丁度ページいっぱいで収まる時)
    return _totalPage;
  }, [totalData, perData]);
  const [namedUser, setNamedUser] = useState('');
  const [sortBy, setSortBy] = useState(sortKey.name);
  const [highlow, setHighlow] = useState(highlowKey.high);
  const [index, setIndex] = useState(0);
  const [activeSort, setActiveSort] = useState('active');
  const prevSearchText = useRef<string | undefined>('');

  const [companyUsersDetailSortBy, setCompanyUsersDetailSortBy] = useState(companyUsersDetailSortKey.company_name);
  const [companyUsersDetailHighlow, setCompanyUsersDetailHighlow] = useState(highlowKey.high);
  const [companyUsersDetailIndex, setCompanyUsersDetailIndex] = useState(1);
  const [companyUsersDetailActiveSort, setCompanyUsersDetailActiveSort] = useState('active');
  const [isSelectCompanyUser, setIsSelectCompanyUser] = useState(false);
  // - フリーワード -
  const changeFreeWord = useCallback((v: string) => {
    setFreeWord(v);
  }, []);

  const deleteCompanyUserAffiliationsData = useCallback(() => {
    setNamedUser('');
    setIsSelectCompanyUser(false);
    setCompanyUsersDetailList([]);
  }, []);
  /**
   * リセット処理
   */
  const dataReset = useCallback(() => {
    setSearchCompanyUsersList([]);
    setCompanyUsersBody({
      current_page: 0,
      last_page: 0,
      per_page: 0,
      total: 0,
    });
    prevSearchText.current = '';
    prevSearchParam.current = {};
    deleteCompanyUserAffiliationsData();
  }, [deleteCompanyUserAffiliationsData]);

  const scrollToRow = useCallback(() => {
    if (rowRef.current) {
      rowRef.current.scrollIntoView({
        block: 'center',
      });
    }
  }, []);

  /**
   * 企業ユーザーデータ所得
   */
  const getSearchCompanyUser = useCallback(async (param?: {
    searchText?: string,
    pageNum?: number,
  }) => {
    const {
      searchText,
      pageNum,
    } = param ?? {};
    const page: number | undefined = (() => {
      if (searchText !== undefined) {
        return undefined;
      }
      return currentPage + 1;
    })();
    const words = searchText !== undefined ? searchText : prevSearchText.current;
    const requestParam = {
      words,
      sort_by: sortBy,
      highlow: highlow,
      page: pageNum ?? page,
      id: (namedUser && (pageNum === undefined)) ? namedUser : undefined,
    };
    return new Promise<ApiGetSearchCompanyUsersResponse>((resolve, reject) => {
      // 再検索を防ぐ
      if (
        param?.pageNum
        && lodash.isEqual(prevSearchParam.current, requestParam)
      ) {
        return;
      }
  
      apiIsp.companyUsers().get(requestParam).then((res => {
        prevSearchParam.current = requestParam;
        resolve(res);
      })).catch(reject);
    })
  }, [currentPage, highlow, namedUser, sortBy]);

  const updataSearchCompanyUserList = useCallback(async (param?: {
    searchText?: string,
    pageNum?: number,
  }) => {
    const {
      searchText,
      pageNum,
    } = param ?? {};
    const words = searchText !== undefined ? searchText : prevSearchText.current;
    if(!words) {
      return;
    }
    const data = await getSearchCompanyUser({ searchText, pageNum });
    const body = data.body.data.data;
    if(body.length) {
      setSearchCompanyUsersList(body);
      setCompanyUsersBody(data.body.data);
      if (namedUser) {
        setCurrentPage(data.body.data.current_page - 1);
        scrollToRow();
      }
      if (freeWord === prevSearchText.current) {
        return;
      }
      if(searchText !== undefined) {
        prevSearchText.current = searchText;
        setCurrentPage(0);
      }
      if (data.body.data.current_page > data.body.data.last_page) {
        setCurrentPage(data.body.data.last_page - 1);
      }
    } else {
      if (searchCompanyUsersList.length) {
        if (checkBlurElement(document.activeElement)) {
          document.activeElement?.blur?.() ;
        }
        dispatch(dialogAction.pushMessage({
          title: '確認',
          message: ['検索結果が見つかりませんでした。'],
          buttons: [
            { label: 'OK', callback: () => dispatch(dialogAction.pop()) }
          ]
        }));
      }
    }
  }, [dispatch, freeWord, getSearchCompanyUser, namedUser, scrollToRow, searchCompanyUsersList.length]);

  /**
   * ユーザー一覧所得
   */
  const setSearchCompanyUserList = useCallback(async (param?: {
    searchText?: string,
    pageNum?: number,
  }, forceUpdate?: boolean,
  ) => {
    const {
      searchText,
      pageNum,
    } = param ?? {};
    if (!freeWord) {
      dataReset();
      return;
    }
    try {
      if (
        searchText === prevSearchText.current
        && searchText !== undefined
        && !forceUpdate
      ) {
        return;
      }
      window.setTimeout(() => {
        updataSearchCompanyUserList({
          searchText,
          pageNum,
        });
      })
    } catch (error) {
      ApiManager.errorFunc(error);
    }
  }, [
    freeWord,
    dataReset,
    updataSearchCompanyUserList,
  ]);

  const getCompanyUserAffiliationsData = useCallback(async (user_id: string) => {
    const data = await apiIsp.companyUsers(user_id).getAffiliations({
      sort_by: companyUsersDetailSortBy,
      highlow: companyUsersDetailHighlow,
    });
    const body = data.body.data.data;
    return body;
  }, [companyUsersDetailHighlow, companyUsersDetailSortBy]);

  const setCompanyUserAffiliationsData = useCallback(async (user_id: string) => {
    const body = await getCompanyUserAffiliationsData(user_id);
    if (body.length) {
      setCompanyUsersDetailList(body);
    } else {
      deleteCompanyUserAffiliationsData();
    }
  }, [deleteCompanyUserAffiliationsData, getCompanyUserAffiliationsData]);

  /** 企業ユーザー一覧テーブルの要素をクリックした際の処理 */
  const handleClickTable = useCallback(async (i: number) => {
    const user_id = searchCompanyUsersList[i].id;
    setIsSelectCompanyUser(true);
    setCompanyUserAffiliationsData(user_id);
    setNamedUser(user_id);
  }, [searchCompanyUsersList, setCompanyUserAffiliationsData]);

  /**
   * 検索処理
   */
  const handleKeyDownSearch = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      deleteCompanyUserAffiliationsData();
      window.setTimeout(() => {
        setSearchCompanyUserList({ searchText: freeWord, pageNum: 0 });
      })
    }
  }, [deleteCompanyUserAffiliationsData, setSearchCompanyUserList, freeWord]);

  /** 表示するページをセット */
  const handlePaginate = useCallback((page: number) => {
    setCurrentPage(page);
    deleteCompanyUserAffiliationsData();
  }, [deleteCompanyUserAffiliationsData]);

  const handleClickReInvite = useCallback((companyCode: string, memberId: string) => {
    const apiModule = apiIsp.companies(companyCode);
    const sendMailPath = `/#/isp/companies/${companyCode}/members/add/`;
    dispatch(dialogAction.pushMessage({
      title: '確認',
      message: ['このユーザーを再招待しますか'],
      buttons: [
        {
          label: 'キャンセル',
          callback: () => {
            dispatch(dialogAction.pop());
          },
        }
        , {
          label: 'OK',
          callback: async () => {
            baseLog('companyAffiliationReInvitationClick');
            dispatch(dialogAction.pop());
            apiModule.members(memberId).reinvite().post({
              pass: sendMailPath,
            }).then((res) => {
              dispatch(dialogAction.pushSendMail({
                message: [
                  '招待メールを送信しました。',
                ],
                callback: () => {
                  dispatch(dialogAction.pop());
                },
              }));
            }).catch((e) => ApiManager.errorFunc(e));
          },
        }],
      }));
    }, [baseLog, dispatch]);

  const updateData = useCallback(async(forceUpdate?: boolean, id?: string) => {
    if (id) {
      const data = await apiIsp.companyUsers().get({
        words: freeWord,
        sort_by: sortBy,
        highlow: highlow,
        id,
      });
      if ((data.body.data.current_page - 1) !== currentPage) {
        setCurrentPage(data.body.data.current_page - 1);
        if(namedUser) {
          setCompanyUserAffiliationsData(namedUser);
          scrollToRow();
        }
        return;
      }
    }
    setSearchCompanyUserList({ searchText: freeWord }, forceUpdate);
    if(namedUser) {
      setCompanyUserAffiliationsData(namedUser);
      scrollToRow();
    }
  }, [
    freeWord,
    namedUser,
    setCompanyUserAffiliationsData,
    setSearchCompanyUserList,
    sortBy,
    highlow,
    currentPage,
    scrollToRow
  ]);

  const handleClickReinviteCancel = useCallback((companyCode: string, memberId: string) => {
    dispatch(dialogAction.pushMessage({
      title: '確認',
      message: ['このユーザーの招待を取消しますか'],
      buttons: [
        {
          label: 'キャンセル',
          callback: () => {
            dispatch(dialogAction.pop());
          },
        },
        {
          label: 'OK',
          callback: () => {
            dispatch(dialogAction.pop());
            // ユーザー削除APi
            apiIsp.companies(companyCode).members(memberId).delete()
              .then(() => {
                dispatch(dialogAction.pushMessage({
                  title: '完了',
                  message: ['招待の取消しが完了しました。'],
                  buttons: [
                    {
                      label: 'OK',
                      callback: async () => {
                        baseLog('companyAffiliationCancelClick');
                        dispatch(dialogAction.pop());
                        updateData(true);
                      },
                    }],
                }));
              })
            .catch((e: any) => ApiManager.errorFunc(e));
          },
        }],
    }));
  }, [dispatch, updateData, namedUser, baseLog]);

  useEffect(() => {
    if (!userInfo.user_id) {
      return;
    }
  }, [userInfo.user_id]);

  useEffect(() => {
    updataSearchCompanyUserList();
  }, [sortBy, highlow, currentPage]);

  useEffect(() => {
    if (namedUser) {
      setCompanyUserAffiliationsData(namedUser);
    }
  }, [namedUser, setCompanyUserAffiliationsData]);

  // クリックされたソートを書き換える
  const updateSortList = useCallback(() => {
    setSortList(prevSortList =>
      prevSortList.map(elm =>
        elm.index === index
          ? {
              ...elm,
              type: highlow ? 'down' : 'up',
              active: activeSort ? 'active' : '',
            }
          : {
            ...elm,
            active: '',
          }
      )
    );
  }, [index, highlow, activeSort]);

  useEffect(() => {
    updateSortList();
  }, [updateSortList]);

  const updateCompanyUsersDetailSortList = useCallback(() => {
    setCompanyUsersDetailSortList(prevSortList =>
      prevSortList.map(elm =>
        elm.index === companyUsersDetailIndex
          ? {
              ...elm,
              type: companyUsersDetailHighlow ? 'down' : 'up',
              active: companyUsersDetailActiveSort ? 'active' : '',
            }
          : {
            ...elm,
            active: '',
          }
      )
    );
  }, [companyUsersDetailIndex, companyUsersDetailHighlow, companyUsersDetailActiveSort]);

  useEffect(() => {
    updateCompanyUsersDetailSortList();
  }, [updateCompanyUsersDetailSortList]);

  // - 編集ボタン -
  const handleClickEditCompanyUser = useCallback((companyCode: string, memberId: string) => {
    dispatch(dialogAction.push({
      title: '企業ユーザー編集',
      element:
      <div className="company_user_dialog__body">
        <CompanyUserEditDialog
          companyCode={companyCode}
          memberId={memberId}
          apiManager={apiManagerIsp}
          callback={async () => {
            baseLog('companyAffiliationEditClick');
            updateData(true, namedUser);
          }}
        />
      </div>
      ,
      className: 'company_user_dialog'
    }));
  }, [baseLog, dispatch, updateData, namedUser]);

  const body = useMemo(() =>
    searchCompanyUsersList.map(element => ([
      <>
        <div className="name">{element.full_name ? element.full_name : ''}</div>
        <div className="kana">{element.full_name_kana ? `(${element.full_name_kana})` : ''}</div>
      </>,
      element.email,
      DateFormatter.date2str(new Date(element.created_at)),
      element.login_at ? DateFormatter.date2str(new Date(element.login_at)) : '',
      <div data-id={element.id} />
    ])),
    [searchCompanyUsersList]
  );

  const companyUsersDetailBody = useMemo(() =>
    companyUsersDetailList.map(element => ([
      element.company_code,
      <>
        <div className="name">{element.company_name ? element.company_name : ''}</div>
        <div className="kana">{element.company_name_kana ? `(${element.company_name_kana})` : ''}</div>
      </>,
      element.display_name,
      (() => {
        switch (String(element.role)) {
          case '1':
            return '管理';
          case '2':
            return 'ユーザー';
          case '3':
            return 'ゲスト';
          default:
            return '';
        }
      })(),
      <div>{element.status === -1 ? '退職' : ''}</div>,
      DateFormatter.date2str(new Date(element.created_at)),
      !element.status ? (
        <ReInviteButton
          handleClickCancelInvite={() => handleClickReinviteCancel(element.company_code, element.member_id)}
          handleClickReInvite={() => handleClickReInvite(element.company_code, element.member_id)}
          isOwner={!!element.is_owner}
        />
      ) : (
        <Button
          size="small"
          color="secondary"
          label="変更"
          onClick={() => handleClickEditCompanyUser(element.company_code, element.member_id)}
        />
      ),
      element.status === -1 ? 'stop' : 'active',
    ])),
    [companyUsersDetailList, handleClickEditCompanyUser, handleClickReInvite, handleClickReinviteCancel]
  );
  return(
    <div
      id="App"
      className="company_users_search"
    >
      <SideBar currentPage="company-user-search" />
      <div className="main_cnt">
        <Header />
        <div className="inner">
          <BreadcrumbList breadcrumbList={breadcrumbList} />
          <section>
            <header>
              <h2>
                企業ユーザー一覧
              </h2>
              <div className="count">
                <span>件数</span>
                {totalData}
                <span>件</span>
              </div>
              <div className='company_user_search'>
                <Input
                  placeholder="名前、名前(カナ)、メールアドレス"
                  onChange={(e) => changeFreeWord(e.target.value)}
                  onKeyDown={(e) => handleKeyDownSearch(e)}
                  className='free_word'
                />
              </div>
            </header>
            {
              Boolean(searchCompanyUsersList.length) && (
                <>
                  <div className="table_box">
                    <Table
                      head={head}
                      body={body}
                      alignList={['c', 'l', 'c', 'c']}
                      selectedIdList={[namedUser]}
                      sortList={sortList}
                      setSortBy={setSortBy}
                      setHighlow={setHighlow}
                      setIndex={setIndex}
                      setActive={setActiveSort}
                      onClickRow={handleClickTable}
                      rowRef={rowRef}
                    />
                  </div>
                  <Pager
                    totalPage={totalPage}
                    currentPage={currentPage}
                    callback={handlePaginate}
                    onChange={() => {}}
                  />
                </>
              )
            }
            <CompanyAffiliationTable
              tableBody={companyUsersDetailBody}
              sortList={companyUsersDetailSortList}
              setSortBy={setCompanyUsersDetailSortBy}
              setHighlow={setCompanyUsersDetailHighlow}
              setIndex={setCompanyUsersDetailIndex}
              setActive={setCompanyUsersDetailActiveSort}
              isSelectCompanyUser={isSelectCompanyUser}
            />
          </section>
        </div>
        {
          !searchCompanyUsersList.length && (
            <div className="company_user_no_data_form">
              <div className="icon-search" />
              <span>検索フォームに入力してください</span>
            </div>
          )
        }
      </div>
    </div>
  );
};
